import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Plus,
  Search,
  Calendar as CalendarIcon,
  Image as ImageIcon,
  BellOff,
  Beaker,
  RefreshCw,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { AppContext } from '../../../context/AppContext';
import {
  PostCallWithErrorResponse,
  simpleGetCallWithErrorResponse,
} from '../../../api/ApiServices';
import { ToastMsg } from '../../TostMsg';
import {
  Button,
  Card,
  Input,
  Table,
  Badge,
  Calendar,
  Modal,
  Pagination,
  ViewIconButton,
  CancelIconButton,
} from '../../../components/ui';
import styles from './PushCampaigns.module.scss';

const ROLE = 'CRM Admin';
const PLATFORM = 'Push';
const PAGE_SIZE = 10;

const pad = (n) => String(n).padStart(2, '0');
const toIsoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const defaultStartDate = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 2);
  return toIsoDay(d);
};
const defaultEndDate = () => toIsoDay(new Date());

const STATUS_VARIANT = {
  sent: 'success',
  success: 'success',
  delivered: 'success',
  sending: 'info',
  scheduled: 'info',
  pending: 'warning',
  queued: 'warning',
  failed: 'danger',
  cancelled: 'danger',
  test: 'neutral',
};

function Thumbnail({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <span className={styles.thumbFallback}>
        <ImageIcon size={16} />
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt || ''}
      className={styles.thumb}
      onError={() => setFailed(true)}
      loading="lazy"
    />
  );
}

const formatScheduled = (date, time) => {
  if (!date && !time) return '—';
  const iso = `${date || ''}${time ? `T${time}` : ''}`;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return [date, time].filter(Boolean).join(' ');
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatTimestamp = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d.getTime())) return String(val);
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatBool = (v) => (v ? 'Yes' : 'No');

export default function PushCampaignsList() {
  const { sidebar, ApiConfigg } = useContext(AppContext);
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [page, setPage] = useState(1);

  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const isPending = (status) =>
    String(status || '').toLowerCase() === 'pending';

  const groupNameById = useMemo(() => {
    const map = new Map();
    groups.forEach((g) => {
      const id = g.groupId ?? g.group_id ?? g.id ?? g._id;
      const name = g.groupName ?? g.group_name ?? g.name;
      if (id != null && name) map.set(String(id), name);
    });
    return map;
  }, [groups]);

  const fetchCampaigns = () => {
    const url = ApiConfigg?.GET_PROMOTIONAL_MESSAGES;
    if (!url) return;
    const parts = [
      `platform=${encodeURIComponent(PLATFORM)}`,
      `role=${ROLE}`,
    ];
    if (search.trim()) parts.push(`search=${encodeURIComponent(search.trim())}`);
    if (startDate) parts.push(`start_date=${encodeURIComponent(startDate)}`);
    if (endDate) parts.push(`end_date=${encodeURIComponent(endDate)}`);

    setLoading(true);
    simpleGetCallWithErrorResponse(`${url}?${parts.join('&')}`)
      .then((res) => {
        const json = res?.json;
        const list = Array.isArray(json?.data) ? json.data : [];
        setCampaigns(list);
        setPage(1);
      })
      .catch((err) => console.error('Push campaigns fetch error:', err))
      .finally(() => setLoading(false));
  };

  const pageCount = Math.max(1, Math.ceil(campaigns.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pagedCampaigns = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return campaigns.slice(start, start + PAGE_SIZE);
  }, [campaigns, safePage]);

  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ApiConfigg?.GET_PROMOTIONAL_MESSAGES]);

  useEffect(() => {
    const url = ApiConfigg?.GET_PROMOTION_GROUPS;
    if (!url) return;
    const qs = [
      `platform=${encodeURIComponent(PLATFORM)}`,
      `page=1`,
      `page_size=200`,
      `role=${ROLE}`,
    ].join('&');
    simpleGetCallWithErrorResponse(`${url}?${qs}`)
      .then((res) => {
        const json = res?.json;
        const list = Array.isArray(json?.data) ? json.data : [];
        setGroups(list);
      })
      .catch((err) => console.error('Push groups fetch error:', err));
  }, [ApiConfigg?.GET_PROMOTION_GROUPS]);

  const handleApplyFilters = (e) => {
    e?.preventDefault?.();
    fetchCampaigns();
  };

  const fetchDetail = (id) => {
    const builder = ApiConfigg?.GET_PROMOTIONAL_MESSAGE_BY_ID;
    if (typeof builder !== 'function' || id == null) return;
    setDetailLoading(true);
    simpleGetCallWithErrorResponse(builder(id))
      .then((res) => {
        const json = res?.json;
        const data = json?.data ?? json;
        setDetail(data && typeof data === 'object' ? data : null);
      })
      .catch((err) => console.error('Push message detail error:', err))
      .finally(() => setDetailLoading(false));
  };

  const openDetail = (id) => {
    setDetailId(id);
    setDetail(null);
    fetchDetail(id);
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetail(null);
  };

  const detailGroupName = useMemo(() => {
    if (!detail) return '';
    const gid = detail.message_group_id;
    if (gid == null) return '—';
    return groupNameById.get(String(gid)) || `Group #${gid}`;
  }, [detail, groupNameById]);

  const detailStatusVariant = detail
    ? STATUS_VARIANT[String(detail.message_status || '').toLowerCase()] || 'info'
    : 'info';

  const openCancelConfirm = (campaign) => setCancelTarget(campaign);
  const closeCancelConfirm = () => {
    if (cancelling) return;
    setCancelTarget(null);
  };

  const handleConfirmCancel = () => {
    if (!cancelTarget) return;
    const builder = ApiConfigg?.POST_CANCEL_PROMOTION_MESSAGE;
    if (typeof builder !== 'function') {
      ToastMsg('error', 'Cancel-message endpoint is not configured.');
      return;
    }
    const url = builder(cancelTarget.id);
    setCancelling(true);
    PostCallWithErrorResponse(url, JSON.stringify({}))
      .then((res) => {
        if (!res?.error) {
          ToastMsg(
            'success',
            res?.json?.message || `"${cancelTarget.message_title}" was cancelled.`
          );
          setCancelTarget(null);
          if (detailId === cancelTarget.id) fetchDetail(cancelTarget.id);
          fetchCampaigns();
        } else {
          ToastMsg('error', res?.json?.message || 'Failed to cancel push message.');
        }
      })
      .catch((err) => {
        console.error('Cancel push message error:', err);
        ToastMsg('error', 'Network error. Please try again.');
      })
      .finally(() => setCancelling(false));
  };

  return (
    <div className={sidebar ? 'taskMain' : 'cx-active taskMain'} id="cx-main">
      <div className={styles.page}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.iconBadge}>
              <Bell size={20} />
            </span>
            <div className={styles.titleBlock}>
              <h1>Push Notification Campaigns</h1>
              <p>Schedule and track promotional push messages sent to your audience.</p>
            </div>
          </div>
          <div className={styles.headerActions}>
            <Button
              variant="primary"
              leftIcon={<Plus size={16} />}
              onClick={() => navigate('/PushCampaigns/create')}
            >
              Create Push Message
            </Button>
          </div>
        </div>

        <Card padding="md" elevation="sm" style={{ marginBottom: 'var(--space-4)' }}>
          <form className={styles.toolbar} onSubmit={handleApplyFilters}>
            <div className={styles.toolbarItemSearch}>
              <Input
                label="Search"
                placeholder="Search by title (e.g. Diwali Offer)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={<Search size={14} />}
              />
            </div>
            <div className={styles.toolbarItemRange}>
              <Calendar
                mode="range"
                label="Date range"
                placeholder="Start date → End date"
                value={[startDate, endDate]}
                onChange={([s, e]) => {
                  setStartDate(s);
                  setEndDate(e);
                }}
              />
            </div>
            <div className={styles.toolbarApply}>
              <Button type="submit" variant="secondary">
                Apply
              </Button>
            </div>
          </form>
        </Card>

        <Modal
          show={detailId != null}
          onHide={closeDetail}
          size="lg"
          title={detail?.message_title || 'Push message details'}
          footer={
            <>
              <Button
                variant="ghost"
                leftIcon={<RefreshCw size={14} />}
                onClick={() => fetchDetail(detailId)}
                disabled={detailLoading}
                loading={detailLoading}
              >
                Refresh
              </Button>
              {detail && isPending(detail.message_status) && (
                <Button
                  variant="danger"
                  leftIcon={<XCircle size={14} />}
                  onClick={() => openCancelConfirm(detail)}
                >
                  Cancel message
                </Button>
              )}
              <Button variant="primary" onClick={closeDetail}>
                Close
              </Button>
            </>
          }
        >
          {detailLoading && !detail ? (
            <div className={styles.detailLoading}>Loading details…</div>
          ) : !detail ? (
            <div className={styles.detailLoading}>No details available.</div>
          ) : (
            <>
              <div className={styles.detailHeader}>
                <Thumbnail
                  src={detail.message_attachment_url}
                  alt={detail.message_attachment_name}
                />
                <div className={styles.detailHeaderBody}>
                  <div className={styles.detailMetaRow}>
                    <Badge variant={detailStatusVariant}>
                      {detail.message_status || 'Scheduled'}
                    </Badge>
                    {detail.is_test_message && (
                      <span className={styles.inlineTag}>
                        <Beaker size={11} /> Test
                      </span>
                    )}
                    {detail.is_silent && (
                      <span className={styles.inlineTag}>
                        <BellOff size={11} /> Silent
                      </span>
                    )}
                    <span className={styles.muted}>ID: {detail.id}</span>
                  </div>
                  <div className={styles.detailTitle}>
                    {detail.message_title || '—'}
                  </div>
                  <div className={styles.detailMessage}>
                    {detail.message || (
                      <span className={styles.detailValueMuted}>No message body</span>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.detailGrid}>
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>Group</span>
                  <span className={styles.detailValue}>
                    {detailGroupName}
                    {detail.message_group_id != null && (
                      <span className={styles.detailValueMuted}>
                        {' '}
                        · #{detail.message_group_id}
                      </span>
                    )}
                  </span>
                </div>
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>Platform</span>
                  <span className={styles.detailValue}>
                    {detail.message_platform || '—'}
                  </span>
                </div>
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>Scheduled</span>
                  <span className={styles.detailValue}>
                    {formatScheduled(
                      detail.message_scheduled_date,
                      detail.message_scheduled_time
                    )}
                  </span>
                </div>
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>Type</span>
                  <span className={styles.detailValue}>
                    {detail.message_type || '—'}
                  </span>
                </div>
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>Created</span>
                  <span className={styles.detailValue}>
                    {formatTimestamp(detail.message_created_at)}
                  </span>
                </div>
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>Updated</span>
                  <span className={styles.detailValue}>
                    {detail.message_updated_at ? (
                      formatTimestamp(detail.message_updated_at)
                    ) : (
                      <span className={styles.detailValueMuted}>—</span>
                    )}
                  </span>
                </div>
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>Sender ID</span>
                  <span className={styles.detailValue}>
                    {detail.message_sender_id ?? (
                      <span className={styles.detailValueMuted}>—</span>
                    )}
                  </span>
                </div>
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>Test message</span>
                  <span className={styles.detailValue}>
                    {formatBool(detail.is_test_message)}
                    {detail.is_test_message && detail.test_number && (
                      <span className={styles.detailValueMuted}>
                        {' '}
                        · {detail.test_number}
                      </span>
                    )}
                  </span>
                </div>
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>Silent push</span>
                  <span className={styles.detailValue}>
                    {formatBool(detail.is_silent)}
                  </span>
                </div>
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>Template ID</span>
                  <span className={styles.detailValue}>
                    {detail.template_id ?? (
                      <span className={styles.detailValueMuted}>—</span>
                    )}
                  </span>
                </div>
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>Source ID</span>
                  <span className={styles.detailValue}>
                    {detail.source_id ?? (
                      <span className={styles.detailValueMuted}>—</span>
                    )}
                  </span>
                </div>
                <div
                  className={`${styles.detailField} ${styles.detailFieldFull}`}
                >
                  <span className={styles.detailLabel}>Message URL</span>
                  <span className={styles.detailValue}>
                    {detail.message_url ? (
                      <a
                        className={styles.detailLink}
                        href={detail.message_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {detail.message_url}
                      </a>
                    ) : (
                      <span className={styles.detailValueMuted}>—</span>
                    )}
                  </span>
                </div>
                <div
                  className={`${styles.detailField} ${styles.detailFieldFull}`}
                >
                  <span className={styles.detailLabel}>Deep link</span>
                  <span className={styles.detailValue}>
                    {detail.deep_link ? (
                      <a
                        className={styles.detailLink}
                        href={detail.deep_link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {detail.deep_link}
                      </a>
                    ) : (
                      <span className={styles.detailValueMuted}>—</span>
                    )}
                  </span>
                </div>
                <div
                  className={`${styles.detailField} ${styles.detailFieldFull}`}
                >
                  <span className={styles.detailLabel}>Attachment URL</span>
                  <span className={styles.detailValue}>
                    {detail.message_attachment_url ? (
                      <a
                        className={styles.detailLink}
                        href={detail.message_attachment_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {detail.message_attachment_url}
                      </a>
                    ) : (
                      <span className={styles.detailValueMuted}>—</span>
                    )}
                  </span>
                </div>
              </div>
            </>
          )}
        </Modal>

        <Modal
          show={!!cancelTarget}
          onHide={closeCancelConfirm}
          size="sm"
          title={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: 'rgba(238, 29, 82, 0.12)',
                  color: 'var(--color-danger)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AlertTriangle size={16} />
              </span>
              Cancel push message?
            </span>
          }
          footer={
            <>
              <Button
                variant="ghost"
                onClick={closeCancelConfirm}
                disabled={cancelling}
              >
                Keep message
              </Button>
              <Button
                variant="danger"
                leftIcon={<XCircle size={14} />}
                onClick={handleConfirmCancel}
                loading={cancelling}
              >
                Yes, cancel it
              </Button>
            </>
          }
        >
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-body)' }}>
            Are you sure you want to cancel{' '}
            <strong style={{ color: 'var(--color-text-strong)' }}>
              "{cancelTarget?.message_title}"
            </strong>
            ? Once cancelled, this push will not be delivered to its target group, and this action cannot be undone.
          </div>
        </Modal>

        <div className={styles.tableWrap}>
          <div className={styles.tableScroll}>
            <Table hoverable>
              <thead>
                <tr>
                  <th style={{ width: 64 }}>Image</th>
                  <th>Title</th>
                  <th>Message</th>
                  <th>Group</th>
                  <th>Scheduled</th>
                  <th>Status</th>
                  <th style={{ width: 200, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className={styles.loadingRow}>
                      Loading push messages…
                    </td>
                  </tr>
                ) : campaigns.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <div className={styles.empty}>
                        <div className={styles.emptyTitle}>No push messages yet</div>
                        <div>Create your first campaign to start engaging your audience.</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  pagedCampaigns.map((c) => {
                    const id = c.id;
                    const title = c.message_title || '—';
                    const message = c.message || '';
                    const img = c.message_attachment_url;
                    const groupId = c.message_group_id;
                    const groupName =
                      (groupId != null && groupNameById.get(String(groupId))) ||
                      (groupId != null ? `Group #${groupId}` : '—');
                    const scheduled = formatScheduled(
                      c.message_scheduled_date,
                      c.message_scheduled_time
                    );
                    const status = c.message_status || 'Scheduled';
                    const variant = STATUS_VARIANT[String(status).toLowerCase()] || 'info';
                    return (
                      <tr key={id}>
                        <td>
                          <Thumbnail src={img} alt={c.message_attachment_name} />
                        </td>
                        <td>
                          <div className={styles.cellTitle}>{title}</div>
                          <div className={styles.cellMeta}>
                            ID: {id}
                            {c.is_test_message && (
                              <span className={styles.inlineTag}>
                                <Beaker size={11} /> Test
                              </span>
                            )}
                            {c.is_silent && (
                              <span className={styles.inlineTag}>
                                <BellOff size={11} /> Silent
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className={styles.cellMessage}>{message}</div>
                          {c.deep_link && (
                            <div className={styles.cellMeta} title={c.deep_link}>
                              ↪ {c.deep_link}
                            </div>
                          )}
                        </td>
                        <td>{groupName}</td>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <CalendarIcon size={14} />
                            {scheduled}
                          </span>
                        </td>
                        <td>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 'var(--space-2)',
                            }}
                          >
                            <Badge variant={variant}>{status}</Badge>
                            {isPending(status) && (
                              <CancelIconButton
                                size="xs"
                                tooltip={`Cancel "${title}"`}
                                onClick={() => openCancelConfirm(c)}
                              />
                            )}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div
                            style={{
                              display: 'inline-flex',
                              gap: 'var(--space-1)',
                              justifyContent: 'flex-end',
                            }}
                          >
                            <ViewIconButton
                              size="sm"
                              tooltip="View details"
                              onClick={() => openDetail(id)}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>

        <Pagination
          page={safePage}
          pageCount={pageCount}
          total={campaigns.length}
          itemLabel="messages"
          onPageChange={(p) => setPage(p)}
        />
      </div>
    </div>
  );
}
